import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {animate, state, style, transition, trigger} from "@angular/animations";
import {EMPTY, switchMap} from "rxjs";
import {ActivatedRoute, Data, Router} from "@angular/router";
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {LIST_OF_REQUESTS} from 'src/app/app-routing.module';
import {TranslocoService} from '@ngneat/transloco';
import {AbstractComponent, ConfirmationModalComponent, ValidationModalComponent} from "@components";
import {Country, Organiser, RequestStatus, UserDetail, Request, RequestTypes} from "@model";
import {RequestService} from "@services";
import {ValidatorResult} from "../../../../../model/validation/ValidationError";
import {FormControl, FormGroup} from "@angular/forms";

@Component({
  selector: 'app-request-details',
  templateUrl: './request-details.component.html',
  styleUrls: ['./request-details.component.scss'],
  animations: [
    trigger('fadeInAnimation', [
      state('in', style({opacity: 1})),
      transition(':enter', [
        style({opacity: 0}),
        animate(1600)
      ])
    ])]
})
export class RequestDetailsComponent extends AbstractComponent implements OnInit, OnDestroy {
  @Input() countries: Country[] = [];
  @Input() requestOrganiser: Organiser | undefined;
  currentRequest: Request | undefined;
  user: UserDetail | undefined;
  isRequestDetailsCollapsed: boolean = false;
  inputForm!: FormGroup;

  constructor(
    private requestService: RequestService,
    private route: ActivatedRoute,
    private router: Router, private translocoService: TranslocoService, private modalService: NgbModal) {
    super();
  }

  ngOnInit(): void {
    this.subsribeToDataChange();
  }

  private subsribeToDataChange(): void {
    this.subscriptions.push(this.route.data.subscribe(({request}: Data) => {
      this.currentRequest = request;
      this.requestOrganiser = this.currentRequest?.organiserRequest;
      this.requestService.currentRequest.next(request);
      this.requestService.fetchRequestUser(this.currentRequest?.id).subscribe((value: UserDetail | undefined) => {
        this.user = value;
      })
      this.inputForm = new FormGroup({
        name: new FormControl(this.requestOrganiser?.name),
        fullName: new FormControl(this.requestOrganiser?.fullName),
        website: new FormControl(this.requestOrganiser?.website),
        email: new FormControl(this.requestOrganiser?.email),
        phone: new FormControl(this.requestOrganiser?.phone),
        address: new FormControl(this.requestOrganiser?.address),
        place: new FormControl(this.requestOrganiser?.place),
        zip: new FormControl(this.requestOrganiser?.zip),
        country: new FormControl(this.getCountry(this.requestOrganiser?.country?.shortCode))
      });
    }));
  }

  getCountry(shortCode?: string): string {
    return this.countries?.filter(country => country?.shortCode === shortCode)[0]?.name ?? "";
  }

  onCancel() {
    this.router.navigate([LIST_OF_REQUESTS]);
  }

  actionsAllowed() {
    return this.currentRequest?.requestStatus === RequestStatus.ON_HOLD
  }

  onModifyRequest(request: Request, accept: boolean): void {
    if (request.type === RequestTypes.ORGANISER) {
      this.requestService.manageOrganiserRequest(request).pipe(
        switchMap((value: boolean) => {
          if (this.currentRequest) {
            this.currentRequest.requestStatus = accept ? RequestStatus.ACCEPTED : RequestStatus.DENIED;
          }
          this.requestService.fetchNewRequests.next(value);
          return this.requestService.fetchRequestById(request.id);
        }),
      ).subscribe((value: Request | null) => {
        this.currentRequest = value ?? undefined;
        this.requestOrganiser = value?.organiserRequest;
      });
    } else if (request.type === RequestTypes.PUBLISH_EVENT) {
      this.requestService.manageEventRequest(request).pipe(
        switchMap((value: ValidatorResult | undefined) => {
          if (value) {
            const modalRef = this.modalService.open(ValidationModalComponent, {
              centered: true,
              size: 'lg'
            });
            modalRef.componentInstance.title = this.translocoService.translate("app");
            modalRef.componentInstance.validatorResult = value
            ;
            modalRef.result.then((_) => {
            }, (_) => {
              /// ignore
            });
            return EMPTY;
          } else {
            this.requestService.fetchNewRequests.next(true);
            return this.requestService.fetchRequestById(request.id);
          }
        }),
      ).subscribe((value: Request | null) => {
        this.currentRequest = value ?? undefined;
        this.requestOrganiser = value?.organiserRequest;
      });
    }
  }
  
  openModifyRequestDialog(accept: boolean) {
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    let requestComment: string = "";
    modalRef.componentInstance.title = this.translocoService.translate("request.list.decline.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("request.list.decline.dialog.text");
    modalRef.componentInstance.dispayTextArea = true;
    modalRef.componentInstance.textAreaPlaceholder = this.translocoService.translate("request.action.add.comment");
    modalRef.componentInstance.textAreInputEvent.subscribe((content: string) => requestComment = content);
    modalRef.result.then((_) => {
      if (this.currentRequest) {
        this.currentRequest.comment = requestComment !== "" ? requestComment : undefined;
        this.onModifyRequest(this.currentRequest, accept);
      }
    }, (_) => {
      /// ignore
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.requestService.currentRequest.next(undefined);
  }
}
